import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import {ProvidersBunchPayload} from "../../gameCenter/providers/slice";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface PaymentProvider {
  id?: number;
  title: string;
  description: string;
  image?: string | File;
  isActive: boolean | number;
}

interface PaymentProvidersState {
  list: PaymentProvider[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: PaymentProvider;
  isItemFetching: boolean;
  processingItemId?: PaymentProvider["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  isActive?: number;
  order: {
    title?: string;
    isActive?: string;
    id?: string;
  };
}

export const getPaymentProvidersList = createAsyncThunk(
  "payment-providers/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.paymentProvidersList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const createPaymentProvider = createAsyncThunk<number, PaymentProvider, { rejectValue: ApiErrorPayload }
> (
  "payment-providers/create",
  async (payload: PaymentProvider, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }
    data.append("title", payload.title ?? "");
    data.append("description", payload.description ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      const result = await instance.post(`${apiRoutes.paymentProviderCreate}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return result.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const updatePaymentProvider = createAsyncThunk<
  boolean,
  PaymentProvider,
  { rejectValue: ApiErrorPayload }
>("payment-providers/update", async (payload: PaymentProvider, { rejectWithValue }) => {
  const data = new FormData();
  if (payload.image instanceof File) {
    const safeName = sanitizeFileName(payload.image.name);

    data.append(
      "image",
      new File([payload.image], safeName, { type: payload.image.type }),
    );
  }
  data.append("title", (payload?.title as string) ?? "");
  data.append("description", (payload.description as string) ?? "");
  data.append("isActive", payload.isActive ? "1" : "0");

  try {
    await instance.put(
      `${apiRoutes.paymentProvidersUpdate}/${payload.id}`,
      data,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return true;
  } catch (error: any) {
      const apiError = error?.data?.error ?? error?.data;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
});
export const getPaymentProvider = createAsyncThunk(
  "payment-providers/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentProvidersItem}/${id}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const deletePaymentProvider = createAsyncThunk(
  "payment-providers/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.paymentProvidersDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updatePaymentProvidersBunch = createAsyncThunk(
    "payment-providers/update-bunch",
    async (data: ProvidersBunchPayload) => {
      try {
        await instance.put(`${apiRoutes.paymentProvidersUpdateBunch}`, {
          ...data,
          isActive: data.isActive,
        });
        return true;
      } catch (error) {
        return false;
      }
    },
);

const initialState: PaymentProvidersState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetching: false,
};

export const paymentProvidersSlice = createSlice({
  name: "payment-providers",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: PaymentProvidersState,
      action: PayloadAction<PaymentProvider>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: PaymentProvidersState) => {
      state.payload = undefined;
      state.isItemFetched = false;
    },
    handlePaginate: (
      state: PaymentProvidersState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (
      state: PaymentProvidersState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PaymentProvidersState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPaymentProvidersList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createPaymentProvider.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createPaymentProvider.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createPaymentProvider.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updatePaymentProvider.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePaymentProvider.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updatePaymentProvider.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getPaymentProvider.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = action.payload;
    });
    builder.addCase(updatePaymentProvidersBunch.fulfilled, (state, action) => {
      if (action.payload === true) {
        state.isListFetched = false;
        state.selectedItems = [];
      }
    });
    builder.addCase(deletePaymentProvider.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deletePaymentProvider.fulfilled, (state, action) => {
      if (action.payload === true) {
        state.isListFetched = false;
      }
      state.processingItemId = undefined;
    });
    builder.addCase(deletePaymentProvider.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
} = paymentProvidersSlice.actions;
